import React, { useEffect, useMemo, useRef, useState } from "react";

const C = {
  gold: "#f7c948",
  dimGold: "#9f7a24",
  dark: "#020202",
  card: "#0b0905",
  line: "rgba(247,201,72,0.26)",
  green: "#22c55e",
  red: "#ef4444",
  emerald: "#064e3b",
  maroon: "#4a0712",
  activeTab: "linear-gradient(180deg, #006b49, #003f2f)",
};

const PRICE_BANDS = [
  { id: "5-100", label: "5 - 100", min: 5, max: 100 },
  { id: "100-200", label: "100 - 200", min: 100, max: 200 },
  { id: "200-300", label: "200 - 300", min: 200, max: 300 },
  { id: "300-400", label: "300 - 400", min: 300, max: 400 },
  { id: "400-500", label: "400 - 500", min: 400, max: 500 },
  { id: "500-600", label: "500 - 600", min: 500, max: 600 },
];

const SCANNER_TABS = [
  { id: "nifty-ce", label: "NIFTY CE", chainKey: "nifty", title: "NIFTY", side: "CE" },
  { id: "nifty-pe", label: "NIFTY PE", chainKey: "nifty", title: "NIFTY", side: "PE" },
  { id: "banknifty-ce", label: "BN CE", chainKey: "banknifty", title: "BANKNIFTY", side: "CE" },
  { id: "banknifty-pe", label: "BN PE", chainKey: "banknifty", title: "BANKNIFTY", side: "PE" },
];

const STATUS_FILTERS = [
  { id: "ALL", label: "All" },
  { id: "Buy Now", label: "Buy Now" },
  { id: "Near SZ", label: "Near SZ" },
  { id: "Away SZ", label: "Away SZ" },
];

function fmt(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function manualSupportKey(chainKey, side, strike) {
  return `${chainKey}-${side}-${Number(strike || 0)}`;
}

function getManualSupportRange(ranges, chainKey, side, strike) {
  const row = ranges?.[manualSupportKey(chainKey, side, strike)];
  const low = Number(row?.supportLow || 0);
  const high = Number(row?.supportHigh || 0);

  if (!low || !high) return null;

  return {
    supportLow: Math.min(low, high),
    supportHigh: Math.max(low, high),
  };
}

function getManualStrikeSettings(ranges, chainKey, side, strike, fallbackSettings) {
  const row = ranges?.[manualSupportKey(chainKey, side, strike)];
  const savedStopLossPrice =
    row?.stopLossPrice !== undefined && row?.stopLossPrice !== ""
      ? Number(row.stopLossPrice)
      : null;

  return {
    quantity: Math.max(1, Number(row?.quantity || fallbackSettings?.quantity || 50)),
    slPoints: Math.max(1, Number(row?.slPoints || fallbackSettings?.slPoints || 15)),
    candleMinutes: Number(row?.candleMinutes || fallbackSettings?.candleMinutes || 1),
    notifyEnabled: row?.notifyEnabled === true,
    stopLossPrice:
      Number.isFinite(savedStopLossPrice) && savedStopLossPrice > 0
        ? savedStopLossPrice
        : null,
  };
}

function calculateRisk(entry, supportLow, scannerSettings) {
  const quantity = Math.max(1, Number(scannerSettings?.quantity || 50));
  const slPoints = Math.max(1, Number(scannerSettings?.slPoints || 15));

  const stopLoss = scannerSettings?.stopLossPrice
    ? Number(scannerSettings.stopLossPrice)
    : Number(Math.max(0.05, supportLow - slPoints).toFixed(2));

  const riskPoints = Number(Math.max(0, entry - stopLoss).toFixed(2));
  const pointsBelowSupport = Number(Math.max(0, supportLow - stopLoss).toFixed(2));

  return {
    quantity,
    riskPoints,
    slPoints: pointsBelowSupport,
    stopLoss,
    totalLoss: Number((quantity * riskPoints).toFixed(2)),
  };
}

function getSupportStatus(entry, support) {
  if (!support) {
    return { label: "Away SZ", color: C.red };
  }

  const supportLow = Number(support.supportLow || 0);
  const supportHigh = Number(support.supportHigh || 0);
  const ltp = Number(entry || 0);

  if (ltp >= supportLow && ltp <= supportHigh) {
    return { label: "Buy Now", color: C.green };
  }

  const distanceFromZone =
    ltp > supportHigh
      ? ltp - supportHigh
      : supportLow - ltp;

  if (distanceFromZone <= 5) {
    return { label: "Near SZ", color: C.gold };
  }

  return { label: "Away SZ", color: C.red };
}

function buildRows(chain, side, priceBand, scannerSettings, chainKey, manualSupportRanges) {
  const sourceRows = side === "CE" ? chain?.calls || [] : chain?.puts || [];

  return sourceRows
    .filter(row => {
      const ltp = Number(row.ltp || 0);
      return ltp >= priceBand.min && ltp <= priceBand.max;
    })
    .map(row => {
      const entry = Number(row.ltp || 0);
      const bid = Number(row.bid || 0);
      const ask = Number(row.ask || 0);
      const manual = getManualSupportRange(manualSupportRanges, chainKey, side, row.strike);

      const rowSettings = getManualStrikeSettings(
        manualSupportRanges,
        chainKey,
        side,
        row.strike,
        scannerSettings
      );

      const supportLow = manual
        ? Number(manual.supportLow.toFixed(2))
        : Number(Math.max(0.05, entry - 8).toFixed(2));

      const supportHigh = manual
        ? Number(manual.supportHigh.toFixed(2))
        : Number(Math.max(supportLow, entry - 3).toFixed(2));

      const range = Number(Math.max(0.05, supportHigh - supportLow).toFixed(2));
      const risk = calculateRisk(entry, supportLow, rowSettings);
      const slPoints = risk.slPoints;
      const stopLoss = risk.stopLoss;

      const support = {
        type: "SUPPORT RANGE",
        supportLow,
        supportHigh,
        range,
        stopLoss,
        touches: 0,
        slPoints,
      };

      const status = getSupportStatus(entry, support);

      return {
        ...row,
        side,
        entry,
        bid,
        ask,
        support,
        risk,
        status,
        aligned: false,
        candleCount: 0,
        candleMinutes: rowSettings.candleMinutes,
        notifyEnabled: rowSettings.notifyEnabled,
        notifyKey: manualSupportKey(chainKey, side, row.strike),
      };
    })
    .sort((a, b) => Number(b.entry || 0) - Number(a.entry || 0));
}

const DEFAULT_SORT = { key: "entry", direction: "desc" };

function toSortNumber(value) {
  return Number(String(value ?? "0").replace(/,/g, "")) || 0;
}

function SortHeading({ label, sortKey, sortConfig, onSort }) {
  const active = sortConfig.key === sortKey;

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-2 font-bold"
      style={{ color: active ? C.green : C.gold }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 13, color: active ? C.green : C.dimGold }}>⇅</span>
    </button>
  );
}

const MANUAL_SUPPORT_ALERT_STORE_KEY = "bloomberg_manual_support_alerts_v1";
const SUPPORT_SCANNER_FETCH_KEY = "bloomberg_support_scanner_fetch";

function loadManualSupportAlertIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(MANUAL_SUPPORT_ALERT_STORE_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveManualSupportAlertIds(ids) {
  localStorage.setItem(MANUAL_SUPPORT_ALERT_STORE_KEY, JSON.stringify([...ids]));
}

function dispatchManualSupportAlert({ id, message }) {
  window.dispatchEvent(
    new CustomEvent("bloomberg-support-alert", {
      detail: {
        id,
        title: "Alert",
        message,
        createdAt: new Date().toISOString(),
      },
    })
  );
}

export default function BloombergSupportScanner({ snapshot, scannerSettings }) {
  const [activeScannerTab, setActiveScannerTab] = useState(SCANNER_TABS[0]);
  const [priceBand, setPriceBand] = useState(PRICE_BANDS[0]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const notifiedManualSupportRef = useRef(loadManualSupportAlertIds());
  const [fetchEnabled, setFetchEnabled] = useState(() => {
    return localStorage.getItem(SUPPORT_SCANNER_FETCH_KEY) !== "STOP";
  });

  const [displaySnapshot, setDisplaySnapshot] = useState(snapshot);

  useEffect(() => {
    if (fetchEnabled) {
      setDisplaySnapshot(snapshot);
    }
  }, [snapshot, fetchEnabled]);

  const side = activeScannerTab.side;
  const chain = displaySnapshot?.optionChains?.[activeScannerTab.chainKey];

  const scannerSettingsForTab = useMemo(
    () => scannerSettings?.[activeScannerTab.chainKey] || {
      quantity: 50,
      slPoints: 15,
      candleMinutes: 1,
    },
    [scannerSettings, activeScannerTab.chainKey]
  );

const manualSupportRanges = useMemo(
  () => scannerSettings?.manualSupportRanges || {},
  [scannerSettings?.manualSupportRanges]
);

const rows = useMemo(
  () => buildRows(
    chain,
    side,
    priceBand,
    scannerSettingsForTab,
    activeScannerTab.chainKey,
    manualSupportRanges
  ),
  [chain, side, priceBand, scannerSettingsForTab, activeScannerTab.chainKey, manualSupportRanges]
);

useEffect(() => {
  rows.forEach(row => {
    if (!row.notifyEnabled || !row.support) return;

    const price = Number(row.entry || 0);
    const supportHigh = Number(row.support.supportHigh || 0);
    const supportLow = Number(row.support.supportLow || 0);
    const stopLoss = Number(row.support.stopLoss || 0);

    const baseId = `${activeScannerTab.chainKey}-${row.side}-${row.strike}-${supportLow}-${supportHigh}-${stopLoss}`;
    const symbol = `${activeScannerTab.title || activeScannerTab.label} ${row.side} ${row.strike}`;

    const fireOnce = (type, message) => {
      const id = `${baseId}-${type}`;
      if (notifiedManualSupportRef.current.has(id)) return;

      notifiedManualSupportRef.current.add(id);
      saveManualSupportAlertIds(notifiedManualSupportRef.current);
      dispatchManualSupportAlert({ id, message });
    };

    if (supportHigh && price <= supportHigh) {
      fireOnce(
        "support-zone-1",
        `${symbol} touched Support Zone 1. Price Rs.${money(price)} reached Rs.${money(supportHigh)}.`
      );
    }

    if (supportLow && price <= supportLow) {
      fireOnce(
        "support-zone-2",
        `${symbol} touched Support Zone 2. Price Rs.${money(price)} reached Rs.${money(supportLow)}.`
      );
    }

    if (stopLoss && price <= stopLoss) {
      fireOnce(
        "stoploss",
        `${symbol} hit StopLoss. Price Rs.${money(price)} reached SL Rs.${money(stopLoss)}.`
      );
    }
  });
}, [rows, activeScannerTab.chainKey, activeScannerTab.label, activeScannerTab.title]);

useEffect(() => {
  setSortConfig(DEFAULT_SORT);
  setStatusFilter("ALL");
}, [displaySnapshot?.updatedAt, activeScannerTab.id, priceBand.id]);

const sortedRows = useMemo(() => {
  const filteredRows =
    statusFilter === "ALL"
      ? rows
      : rows.filter(row => row.status?.label === statusFilter);

  return [...filteredRows].sort((a, b) => {
    const aValue = toSortNumber(a[sortConfig.key]);
    const bValue = toSortNumber(b[sortConfig.key]);

    return sortConfig.direction === "asc"
      ? aValue - bValue
      : bValue - aValue;
  });
}, [rows, sortConfig, statusFilter]);

const toggleSort = key => {
  setSortConfig(prev => {
    if (prev.key === key) {
      return {
        key,
        direction: prev.direction === "desc" ? "asc" : "desc",
      };
    }

    return { key, direction: "desc" };
  });
};

const toggleFetching = () => {

  const next = !fetchEnabled;

  setFetchEnabled(next);

  localStorage.setItem(
    SUPPORT_SCANNER_FETCH_KEY,
    next ? "START" : "STOP"
  );
};

  return (
    <div className="mt-5 rounded-lg border p-4" style={{ background: C.card, borderColor: C.line, color: C.gold }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.35em]" style={{ color: C.dimGold }}>
            NSE OPTION CHAIN SUPPORT SCANNER
          </div>
          <h2 className="mt-2 text-2xl font-bold">
            {activeScannerTab.title} {side} Support Scanner
          </h2>
        </div>

        <button
          type="button"
          onClick={toggleFetching}
          className="rounded px-4 py-2 font-bold"
          style={{
            background: fetchEnabled ? "#8b0000" : "#006b49",
            color: "#fff",
            border: `1px solid ${fetchEnabled ? "#ff5555" : "#22c55e"}`
          }}
        >
          {fetchEnabled ? "STOP" : "START"}
        </button>

        <div className="flex flex-wrap justify-end gap-2">
          {SCANNER_TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveScannerTab(tab)}
              className={`rounded px-5 py-2 font-bold ${activeScannerTab.id === tab.id ? "terminal-tab-active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap justify-end gap-2">
          {PRICE_BANDS.map(band => (
            <button
              key={band.id}
              type="button"
              onClick={() => setPriceBand(band)}
              className={`rounded px-4 py-2 text-xs font-bold ${priceBand.id === band.id ? "terminal-tab-active" : ""}`}
              style={{
                background: priceBand.id === band.id ? C.activeTab : "#020202",
                border: `1px solid ${priceBand.id === band.id ? C.green : C.line}`,
                color: priceBand.id === band.id ? "#ffffff" : C.gold,
                boxShadow: priceBand.id === band.id ? "0 0 16px rgba(0,107,73,0.75)" : "none",
              }}
            >
              Rs.{band.label}
            </button>
          ))}
        </div>

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            {STATUS_FILTERS.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => setStatusFilter(item.id)}
                className={`rounded px-4 py-2 text-xs font-bold ${statusFilter === item.id ? "terminal-tab-active" : ""}`}
                style={{
                  background: statusFilter === item.id ? C.activeTab : "#020202",
                  border: `1px solid ${statusFilter === item.id ? C.green : C.line}`,
                  color: statusFilter === item.id ? "#ffffff" : C.gold,
                  boxShadow: statusFilter === item.id ? "0 0 16px rgba(0,107,73,0.75)" : "none",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

      </div>

      <div className="mt-4 grid grid-cols-4 gap-3 text-sm">
        <div>
          <div style={{ color: C.dimGold }}>Spot</div>
          <div className="text-xl font-bold">{fmt(chain?.spot)}</div>
        </div>
        <div>
          <div style={{ color: C.dimGold }}>Expiry</div>
          <div className="text-xl font-bold">{chain?.expiry || "--"}</div>
        </div>
        <div>
          <div style={{ color: C.dimGold }}>PCR</div>
          <div className="text-xl font-bold">{chain?.pcr || 0}</div>
        </div>
        <div>
          <div style={{ color: C.dimGold }}>Rows</div>
          <div className="text-xl font-bold">{sortedRows.length}</div>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.line}` }}>
              <th className="px-3 py-3 text-left">
                <SortHeading label="Strike" sortKey="strike" sortConfig={sortConfig} onSort={toggleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeading label="Entry" sortKey="entry" sortConfig={sortConfig} onSort={toggleSort} />
              </th>
              <th className="px-3 py-3 text-left">Support Range</th>
              <th className="px-3 py-3 text-left">SL</th>
              <th className="px-3 py-3 text-left">Qty / Risk / Loss</th>
              <th className="px-3 py-3 text-left">
                <SortHeading label="OI" sortKey="oi" sortConfig={sortConfig} onSort={toggleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeading label="OI Chg" sortKey="oiChange" sortConfig={sortConfig} onSort={toggleSort} />
              </th>
              <th className="px-3 py-3 text-left">
                <SortHeading label="Volume" sortKey="volume" sortConfig={sortConfig} onSort={toggleSort} />
              </th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-left">Alignment</th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.map(row => (
              <tr key={`${row.side}-${row.strike}`} style={{ borderBottom: `1px solid ${C.line}` }}>
                <td className="px-3 py-3 font-bold">{row.side} {row.strike}</td>

                <td className="px-3 py-3 font-bold">
                Rs.{money(row.entry)}
                </td>

                <td className="px-3 py-3" style={{ color: row.support ? C.green : C.dimGold }}>
                  {row.support
                    ? `${row.support.type || "SUPPORT RANGE"}: Rs.${money(row.support.supportLow)} - Rs.${money(row.support.supportHigh)}`
                    : "No clear support zone"}
                </td>

                <td className="px-3 py-3" style={{ color: C.red }}>
                  {row.support && row.risk
                    ? `SL: Rs.${money(row.risk.stopLoss)} (${row.risk.slPoints} points below support)`
                    : "--"}
                </td>

                <td className="px-3 py-3" style={{ color: C.red }}>
                  {row.risk
                    ? `Qty: ${row.risk.quantity} | Risk: ${row.risk.riskPoints} pts | SL: ${row.risk.slPoints} pts | Loss: Rs.${money(row.risk.totalLoss)}`
                    : "--"}
                </td>

                <td className="px-3 py-3">{fmt(row.oi)}</td>

                <td className="px-3 py-3" style={{ color: row.oiChange >= 0 ? C.green : C.red }}>
                {row.oiChange >= 0 ? "+" : ""}{fmt(row.oiChange)}
                </td>

                <td className="px-3 py-3">{fmt(row.volume)}</td>

                <td className="px-3 py-3 font-bold" style={{ color: row.status.color }}>
                {row.status.label}
                </td>

                <td className="px-3 py-3" style={{ color: C.dimGold }}>
                  {row.support ? (
                    <>
                      Touches: {row.support.touches} | Range: {row.support.range} pts | Candles: {row.candleCount || 0}
                      <br />
                      {row.aligned ? "Aligned with market data" : "Not aligned / wait"}
                    </>
                  ) : (
                    <>
                      Candles checked: {row.candleCount || 0}
                      <br />
                      No clear support
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!sortedRows.length && (
          <div className="py-10 text-center" style={{ color: C.dimGold }}>
            No {activeScannerTab.title} {side} premium rows found between Rs.{priceBand.min} and Rs.{priceBand.max}.
          </div>
        )}
      </div>
    </div>
  );
}
